import DeleteAlert from '@/components/DeleteAlert'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import SupplementDialog from '@/components/supplements/supplementDialog'
import {
  columns,
  SUPPLEMENT_CATEGORY_OPTIONS,
  type Supplement,
} from '@/components/supplements/supplementColumns'
import { Button } from '@/components/ui/button'
import {
  useHasPermission,
  useOrg,
  useUserId,
} from '@/lib/states/session/hooks'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { patchListCache, removeFromList } from '@trainrr/api-client'
import { canDeleteOrgLibraryItem } from '@/lib/libraryAccess'
import { createFileRoute } from '@tanstack/react-router'
import { Pill, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'

export const Route = createFileRoute('/_auth/_dashboard/supplements')({
  component: SupplementsPage,
})

function SupplementsPage() {
  const org = useOrg()
  const userId = useUserId()
  const canManageLibrary = useHasPermission('library.edit')
  const tableColumns = useMemo(
    () => columns({ userId, canManageLibrary }),
    [userId, canManageLibrary],
  )
  const [open, setOpen] = useState(false)
  const [supplement, setSupplement] = useState<Supplement | undefined>()
  const [supplementToDelete, setSupplementToDelete] = useState<
    Supplement | undefined
  >()

  const {
    data: supplements,
    isLoading,
    error,
  } = trpc.supplements.list.useQuery(undefined, {
    enabled: Boolean(org?.id),
  })
  const utils = trpc.useUtils()
  const deleteSupplement = trpc.supplements.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.supplements.list, (old) =>
        removeFromList(old, variables.id),
      )
      setSupplementToDelete(undefined)
    },
    onError: (mutationError) => {
      errorToast(mutationError)
    },
  })

  if (error) {
    console.error('error fetching supplements', error)
  }

  const sourceFilterOptions = useMemo(
    () => [
      { value: 'system', label: 'Trainrr' },
      { value: 'org', label: 'Your org' },
    ],
    [],
  )

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSupplement(undefined)
    }
  }

  const handleSelectSupplement = (selectedSupplement: Supplement) => {
    if (open) return
    setSupplement(selectedSupplement)
    setOpen(true)
  }

  return (
    <>
      <DeleteAlert
        title="Delete supplement"
        description="Are you sure you want to delete this supplement? This action cannot be undone."
        open={supplementToDelete !== undefined}
        onCancel={() => setSupplementToDelete(undefined)}
        onDelete={() => {
          if (
            !supplementToDelete?.id ||
            !canDeleteOrgLibraryItem(supplementToDelete, {
              userId,
              canManageLibrary,
            })
          ) {
            setSupplementToDelete(undefined)
            return
          }
          deleteSupplement.mutate({ id: supplementToDelete.id })
        }}
      />

      <SupplementDialog
        open={open}
        onOpenChange={handleOpenChange}
        supplement={supplement}
      />

      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Supplements</h2>

              <Button className="gap-2" onClick={() => setOpen(true)}>
                <Plus className="h-4 w-4" /> Add New
              </Button>
            </div>

            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={tableColumns}
              data={supplements ?? []}
              onRowSelected={handleSelectSupplement}
              onRowDeleted={(selected) => {
                if (
                  !canDeleteOrgLibraryItem(selected, {
                    userId,
                    canManageLibrary,
                  })
                ) {
                  return
                }
                setSupplementToDelete(selected)
              }}
              showSearchBar
              filterOptions={[
                {
                  column: 'category',
                  title: 'Category',
                  options: [...SUPPLEMENT_CATEGORY_OPTIONS],
                  singleSelect: true,
                  includeAllOption: true,
                },
                {
                  column: 'source',
                  title: 'Source',
                  options: sourceFilterOptions,
                  singleSelect: true,
                  includeAllOption: true,
                },
              ]}
              stickyHeader
              isLoading={isLoading}
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              tableClassName="table-fixed min-w-[872px]"
              virtualizedHeight="100%"
              estimateRowHeight={88}
              emptyView={
                <Empty className="border border-dashed">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Pill className="h-5 w-5" />
                    </EmptyMedia>
                    <EmptyTitle>No supplements yet</EmptyTitle>
                    <EmptyDescription>
                      Save commonly used supplements so coaches can reuse them
                      across client plans.
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button className="gap-2" onClick={() => setOpen(true)}>
                      <Plus className="h-4 w-4" /> Create supplement
                    </Button>
                  </EmptyContent>
                </Empty>
              }
            />
          </div>
        </div>
      </div>
    </>
  )
}
